import React, { MouseEvent, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailAttachment from "@mapmycustomers/shared/types/email/EmailAttachment";
import { formatFileSize } from "@mapmycustomers/shared/util/formatters";
import { Button, FileIcon } from "@mapmycustomers/ui";

import { fetchEmailAttachment } from "@app/component/EntityEmails/store/actions";
import { downloadFileByUrl } from "@app/util/file";

import styles from "./EmailAttachmentView.module.scss";
import messages from "./messages";

interface EmailAttachmentProps {
  attachment: EmailAttachment;
  messageId: Email["messageId"];
  onFetchAttachment: typeof fetchEmailAttachment.request;
}

const EmailAttachmentView: React.FC<EmailAttachmentProps> = ({
  attachment,
  messageId,
  onFetchAttachment,
}) => {
  const intl = useIntl();
  const [downloading, setDownloading] = useState(false);

  const fileIconFile = useMemo(() => {
    return {
      contentType: attachment.contentType,
      name: attachment.filename,
    };
  }, [attachment]);

  const fileTitle = `${attachment.filename} (${formatFileSize(attachment.size)})`;

  const handleDownload = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setDownloading(true);
      onFetchAttachment({
        attachmentId: attachment.id,
        messageId,
        onFailure: () => {
          setDownloading(false);
        },
        onSuccess: (file: Blob) => {
          setDownloading(false);
          downloadFileByUrl(window.URL.createObjectURL(file), attachment.filename);
        },
      });
    },
    [attachment, messageId, onFetchAttachment, setDownloading]
  );

  return (
    <div className={styles.container}>
      <FileIcon className={styles.fileIcon} file={fileIconFile} />
      <Tooltip title={fileTitle}>
        <span className={styles.fileName}>{fileTitle}</span>
      </Tooltip>
      <Tooltip title={intl.formatMessage(messages.downloadAttachment)}>
        <Button
          className={styles.downloadButton}
          icon={<FontAwesomeIcon icon={faDownload} />}
          loading={downloading}
          onClick={handleDownload}
          type="text"
        />
      </Tooltip>
    </div>
  );
};

const mapDispatchToProps = {
  onFetchAttachment: fetchEmailAttachment.request,
};

export default connect(null, mapDispatchToProps)(EmailAttachmentView);
